import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Image as Img } from '@cp/ui';
import { Stack } from '@cp/ds/src/components/stack';
import { IconButton } from '@cp/ds/src/components/iconButton';
import { styled } from '@cp/ds/src/theme/styled';
import { ChevronLeftIcon } from '@cp/ds/src/icons/chevronLeft';
import { ChevronRightIcon } from '@cp/ds/src/icons/chevronRight';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { ClientPhotoSize } from '@common/const/client/photo';
import { ClientPhotoViewModel } from '@common/model/client/photo/view';
import { ImageAssetItem } from '@common/model/resource/image';
import { getResizedAssetItem, getResizedAssetItems } from '@cp/shared/image';
import { useIsMobile } from '@cp/ds/src/hooks';

const TRANSITION_DURATION = 300;
const SLIDE_WIDTH_PERCENT = 100;

const SliderContainer = styled(Stack)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    maxWidth: '334px',
  },
}));

const SlidesWrapper = styled('div')<{ isTransitioning: boolean }>(({ isTransitioning }) => ({
  display: 'flex',
  width: '100%',
  transition: isTransitioning ? `transform ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
  cursor: 'grab',
  userSelect: 'none',
  pointerEvents: 'auto',
  position: 'relative',
  zIndex: 1,
  '&:active': {
    cursor: 'grabbing',
  },
}));

const SlideContainer = styled(Stack)({
  position: 'relative',
  width: '100%',
  minWidth: '100%',
  height: 'auto',
});

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 3,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: theme.palette['gray dark'],
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  backdropFilter: 'blur(4px)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  '&:active': {
    transform: 'translateY(-50%) scale(0.95)',
  },
  width: '32px',
  height: '32px',
  minWidth: '32px',
  minHeight: '32px',
}));

const PrevButton = styled(NavigationButton)({
  left: '8px',
});

const NextButton = styled(NavigationButton)({
  right: '8px',
});

const DotsContainer = styled(Stack)({
  position: 'absolute',
  bottom: '8px',
  left: '50%',
  transform: 'translateX(-50%)',
  flexDirection: 'row',
  gap: '4px',
  zIndex: 3,
});

const Dot = styled('div')<{ active: boolean }>(({ theme, active }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: active ? theme.palette.white : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: active ? 'scale(1.2)' : 'scale(1)',
  '&:hover': {
    backgroundColor: active ? theme.palette.white : 'rgba(255, 255, 255, 0.8)',
    transform: 'scale(1.1)',
  },
}));

interface ImageSliderProps {
  mainPhoto?: ClientPhotoViewModel;
  photoSet?: ClientPhotoViewModel[];
  alt: string;
  children?: React.ReactNode;
}

interface PointerEventLike {
  clientX: number;
  clientY: number;
  pointerType?: string;
  preventDefault?: () => void;
}

const mainPhotoToAsset = (photo: ClientPhotoViewModel, size: number): ImageAssetItem => {
  const resizedAsset = getResizedAssetItem(photo.image.base, size);
  return resizedAsset || { src: photo.notCroppedOriginalSrc as string, width: size, height: size };
};

export const ImageSlider: React.FC<ImageSliderProps> = ({ mainPhoto, photoSet, alt, children }) => {
  const isMobile = useIsMobile();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isDragging = useRef(false);

  const slides = useMemo(() => {
    const result: { photo: ClientPhotoViewModel; type: 'avatar' | 'resume' }[] = [];

    const avatarPhoto = photoSet?.find((p) => p.purpose === ClientPhotoPurpose.Avatar);
    const resumePhoto = photoSet?.find((p) => p.purpose === ClientPhotoPurpose.Resume);

    const firstPhoto = avatarPhoto || mainPhoto;
    if (firstPhoto) {
      result.push({ photo: firstPhoto, type: 'avatar' });
    }

    if (resumePhoto && resumePhoto.id !== firstPhoto?.id) {
      result.push({ photo: resumePhoto, type: 'resume' });
    }

    return result;
  }, [mainPhoto, photoSet]);

  const slidesWithAssets = useMemo(() => {
    return slides.map((slide) => ({
      ...slide,
      assets: getResizedAssetItems(slide.photo.image, ClientPhotoSize.Medium),
      mainAsset: mainPhotoToAsset(slide.photo, ClientPhotoSize.Medium),
    }));
  }, [slides]);

  const goToSlide = useCallback(
    (index: number) => {
      if (index === currentSlide || isTransitioning) return;

      setIsTransitioning(true);
      setCurrentSlide(index);

      setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION);
    },
    [currentSlide, isTransitioning],
  );

  const handlePrevSlide = useCallback(() => {
    const prevIndex = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    goToSlide(prevIndex);
  }, [currentSlide, slides.length, goToSlide]);

  const handleNextSlide = useCallback(() => {
    const nextIndex = currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
    goToSlide(nextIndex);
  }, [currentSlide, slides.length, goToSlide]);

  const handleDotClick = useCallback(
    (index: number) => {
      goToSlide(index);
    },
    [goToSlide],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent | PointerEventLike) => {
      if (slides.length <= 1) return;

      touchStartX.current = e.clientX;
      touchStartY.current = e.clientY;
      isDragging.current = false;
    },
    [slides.length],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent | PointerEventLike) => {
      if (touchStartX.current === null || touchStartY.current === null || slides.length <= 1) return;

      const deltaX = Math.abs(e.clientX - touchStartX.current);
      const deltaY = Math.abs(e.clientY - touchStartY.current);

      if (deltaX > 10 || deltaY > 10) {
        isDragging.current = true;

        if (deltaX > deltaY && e.preventDefault) {
          e.preventDefault();
        }
      }
    },
    [slides.length],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent | PointerEventLike) => {
      if (touchStartX.current === null || touchStartY.current === null || slides.length <= 1) return;

      const deltaX = touchStartX.current - e.clientX;
      const deltaY = Math.abs(touchStartY.current - e.clientY);
      const minSwipeDistance = 50;

      const isHorizontalSwipe = Math.abs(deltaX) > deltaY;
      const isLongEnoughSwipe = Math.abs(deltaX) > minSwipeDistance;

      if (isLongEnoughSwipe && isHorizontalSwipe) {
        if (deltaX > 0) {
          handleNextSlide();
        } else {
          handlePrevSlide();
        }
      }

      touchStartX.current = null;
      touchStartY.current = null;
      isDragging.current = false;
    },
    [handleNextSlide, handlePrevSlide, slides.length],
  );

  if (slides.length === 0) {
    return null;
  }

  return (
    <SliderContainer>
      <SlidesWrapper
        isTransitioning={isTransitioning}
        onPointerCancel={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerLeave={handlePointerUp}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTouchEnd={(e) => {
          const touch = e.changedTouches[0];
          handlePointerUp({
            clientX: touch.clientX,
            clientY: touch.clientY,
            pointerType: 'touch',
          });
        }}
        onTouchMove={(e) => {
          const touch = e.touches[0];
          handlePointerMove({
            clientX: touch.clientX,
            clientY: touch.clientY,
            pointerType: 'touch',
            preventDefault: () => e.preventDefault(),
          });
        }}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          handlePointerDown({
            clientX: touch.clientX,
            clientY: touch.clientY,
            pointerType: 'touch',
          });
        }}
        style={{
          transform: `translateX(-${currentSlide * SLIDE_WIDTH_PERCENT}%)`,
          pointerEvents: 'auto',
          touchAction: 'pan-y',
        }}
      >
        {slidesWithAssets.map((slide, index) => (
          <SlideContainer key={slide.photo.id}>
            <picture>
              {slide.assets.map((asset) => (
                <source key={asset.src} srcSet={asset.src} type={asset.mimeType} />
              ))}
              <Img
                alt={alt}
                height={slide.mainAsset?.height}
                src={slide.mainAsset?.src}
                sx={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                width={slide.mainAsset?.width}
              />
            </picture>
          </SlideContainer>
        ))}
      </SlidesWrapper>

      <Stack sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
        <Stack sx={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'auto' }}>{children}</Stack>
      </Stack>

      {slides.length > 1 && !isMobile && (
        <>
          <PrevButton onClick={handlePrevSlide}>
            <ChevronLeftIcon />
          </PrevButton>
          <NextButton onClick={handleNextSlide}>
            <ChevronRightIcon />
          </NextButton>
        </>
      )}

      {slides.length > 1 && (
        <DotsContainer>
          {slides.map((_, index) => (
            <Dot active={index === currentSlide} key={index} onClick={() => handleDotClick(index)} />
          ))}
        </DotsContainer>
      )}
    </SliderContainer>
  );
};
